import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getKycStatus } from "../services/kycService";
import { handleApiError } from "../utils/handleApiError";

const KycContext = createContext(null);

export function KycProvider({ children }) {
  const [kycStatus, setKycStatus] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("kycStatus") || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // true until first API call completes — prevents premature redirect
  const [initializing, setInitializing] = useState(() => !!localStorage.getItem("token"));

  const refreshKycStatus = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setKycStatus(null);
      return null;
    }

    setLoading(true);
    try {
      const res = await getKycStatus();
      const data = res.data?.data || null;
      if (data?.reupload_request?.reason && data?.reupload_request?.status === "pending") {
        toast.error(`Re-upload Required: ${data.reupload_request.reason}`, { autoClose: false });
      }
      setKycStatus(data);
      localStorage.setItem("kycStatus", JSON.stringify(data));
      setError(null);
      return data;
    } catch (err) {
      const message = handleApiError(err) || "Failed to fetch KYC status.";
      setError(message);
      return null;
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("token")) {
      refreshKycStatus();
    }
  }, []);

  const isRestricted = useMemo(() => {
    if (initializing) return false;
    if (!kycStatus) return true;
    if (kycStatus.kyc_submitted === false || kycStatus.documents_submitted === false) return true;
    const verified = kycStatus.kyc_status === "verified" || kycStatus.kyc_status === "complete" || kycStatus.kyc_status === "approved";
    if (verified) return false;
    return true;
  }, [kycStatus, initializing]);

  const isVerified = useMemo(() => {
    if (!kycStatus) return false;
    return kycStatus.kyc_status === "verified" || kycStatus.kyc_status === "complete" || kycStatus.kyc_status === "approved";
  }, [kycStatus]);

  const value = useMemo(
    () => ({ kycStatus, setKycStatus, loading, initializing, error, refreshKycStatus, isRestricted, isVerified }),
    [kycStatus, loading, initializing, error, isRestricted, isVerified]
  );

  return <KycContext.Provider value={value}>{children}</KycContext.Provider>;
}

export function useKyc() {
  const context = useContext(KycContext);
  if (!context) {
    throw new Error("useKyc must be used within KycProvider");
  }
  return context;
}
